import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Badge, Button, Select, Space, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import {
  RENEWAL_STATUS_DISPLAY,
  RENEWAL_STATUS_DISPLAY_SUBSET,
  RENEWAL_STATUSES_MAP,
} from "../../../shared/constants";
import dayjs from "dayjs";

const { Text } = Typography;
const HEIGHT = 24;
const WIDTH = 160;

export default function InlineEditableStatus({
  value,
  onSave,
  disabled,
  sentAt,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "not_sent");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "not_sent");
  }, [value, editing]);

  const handleSave = () => {
    setEditing(false);
    if (draft === value) return;
    onSave?.(draft.trim());
  };

  const handleCancel = () => {
    setEditing(false);

    setDraft(value ?? "not_sent");
  };

  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (!wrapRef.current) return handleCancel();
    if (next && wrapRef.current.contains(next)) return;
    handleCancel();
  };

  const selectOptions = Object.entries(RENEWAL_STATUS_DISPLAY_SUBSET).map(
    ([backend, label]) => ({
      label,
      value: backend,
    })
  );

  if (editing && !disabled) {
    return (
      <div
        ref={wrapRef}
        onBlur={handleBlur}
        style={{ display: "inline-block", lineHeight: 0 }}
      >
        <Space.Compact size="small">
          <Select
            size="small"
            style={{ width: WIDTH, height: HEIGHT }}
            value={draft}
            onChange={(v) => setDraft(v)}
            options={selectOptions}
          />
          <Button
            type="default"
            style={{
              marginLeft: 1,
              height: HEIGHT,
              color: "#389e0d",
              background: "#f6ffed",
              borderColor: "#b7eb8f",
            }}
            icon={<CheckOutlined />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSave}
          />
          <Button
            type="default"
            style={{
              height: HEIGHT,
              color: "#cf1322",
              background: "#fff1f0",
              borderColor: "#ffa39e",
            }}
            icon={<CloseOutlined />}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCancel}
          />
        </Space.Compact>
      </div>
    );
  }

  const statusDisplay = RENEWAL_STATUS_DISPLAY[value] || "Not Sent";
  const badgeText =
    statusDisplay == RENEWAL_STATUS_DISPLAY["sent"] && sentAt
      ? `${statusDisplay} (${dayjs(sentAt).format("MMM D, YYYY")})`
      : statusDisplay;

  return (
    <Tooltip title={disabled ? "" : "Click to edit"}>
      <span
        role={disabled ? "text" : "button"}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " "))
            setEditing(true);
        }}
        style={{
          width: WIDTH,
          height: HEIGHT,
          display: "inline-flex",
          alignItems: "center",
          padding: "0 7px",
          border: "1px solid transparent",
          borderRadius: 0,
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = "#d9d9d9";
          e.currentTarget.style.background = "#f5f5f5";
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.background = "transparent";
        }}
      >
        <Badge
          status={RENEWAL_STATUSES_MAP[value] || "default"}
          text={<Text>{badgeText}</Text>}
        />
      </span>
    </Tooltip>
  );
}
